import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateCustomAttributeQueries } from '@/modules/common/utils/query-invalidation';

import {
  createCustomAttribute,
  CreateCustomAttributeInput,
  updateCustomAttribute,
  UpdateCustomAttributeInput,
} from '../../api/custom-attributes.api';

/**
 * Payload describing the diff to persist on save:
 * - `create`: brand-new attribute rows (no backend id yet)
 * - `update`: existing rows whose name or option list changed (carry the backend id)
 *
 * Deletions are handled separately via the delete confirm modal, not here.
 */
export interface SaveCustomAttributesPayload {
  create: CreateCustomAttributeInput[];
  update: (UpdateCustomAttributeInput & { id: number })[];
}

/**
 * Persists the custom attributes diff by fanning out one request per changed row:
 * POST for new rows, PATCH for edited rows. The backend exposes no bulk endpoint.
 */
export const useSaveCustomAttributes = () => {
  const queryClient = useQueryClient();
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: async ({ create, update }: SaveCustomAttributesPayload) => {
      await Promise.all([
        ...create.map(input => createCustomAttribute(input)),
        ...update.map(({ id, ...input }) => updateCustomAttribute(id, input)),
      ]);
    },
    onSuccess: async () => {
      await invalidateCustomAttributeQueries(queryClient);
    },
    onError: error =>
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.settings:api.error.customAttributes.saveFailed'
      ),
  });
};
